/**
 * `trip_plan` — the hero of the board.
 *
 * A vertical ribbon per day: the arrival and the gates unnumbered, then the
 * numbered stops with their hours, with breaks nested under the stop they
 * follow.
 *
 * **Nothing here is recomputed.** The numbers are the visitor's own — «третья
 * точка» means the third SIGHT, and the museum's own arithmetic put it there —
 * so the component renders the `n` it is given, renders no number where there is
 * none, and never re-sorts. The same rule the Telegram renderer follows. The
 * walking minutes are 2GIS's, and the totals are the graph's.
 */

import type { TripPlanProps } from '../generated/schemas';

// The catalogue nests these rather than naming them, so they are read off the
// generated type instead of being re-declared beside it.
type PlanDay = TripPlanProps['days'][number];
type PlanItem = NonNullable<PlanDay['items']>[number];
import { send } from '../../lib/send';
import { requestRemoval, usePlanRemoval } from '../planRemoval';
import phrases from '../../lib/generated/phrases.json';
import { cn } from '../../lib/cn';
import { stripEmoji as plain } from '../../lib/emoji';
import { useSendLocked } from '../../chat/turnLock';
import CardActions from './CardActions';
import { useCardLive } from '../inlineCard';
// The badge words and the totals line are the export's too (#39): one table, so
// the saved PDF says what this card says.
import { KIND_WORD, STATUS_LABEL, planTotals } from '../../export/planExport';
import type { ReactNode } from 'react';

// The unnumbered rows have no picture of their own: the marker is a shape drawn by
// CSS (`plan__marker--arrival|entrance|break`), and the word is spoken in the row.

// A break's ✕ (INV-PH-116) names ONE break: its own kind word — the row's title before « · »
// («Обед», «Кофе или мороженое») — and the stop it follows, the nearest stop row above it on the
// card (`breaks.card_item`, after its stop). The phrase is the graph's (`breaks.REMOVE_ASK`,
// exported to `phrases.json`) — two breaks after one stop are not both removed by one ✕. Without a
// stop above (a break first in a day) there is no phrase to send, and no ✕.
export function breakRemoveAsk(title: string, after: string): string {
  const kind = plain(title).split(' · ')[0]!.trim();
  return phrases.plan.break_remove_ask.replace('{kind}', kind).replace('{after}', after);
}

function Row({ item, editable, after }: { item: PlanItem; editable: boolean; after?: string }) {
  const numbered = typeof item.n === 'number';
  const statusLabel = item.status ? STATUS_LABEL[item.status] : undefined;
  // A ✕ pressed and not yet answered (INV-PH-114): this row says «Убираю…», every other ✕ waits.
  const waiting = usePlanRemoval((state) => state.pending);
  const removing = waiting !== null && waiting.title === item.title;
  const locked = waiting !== null && !removing;
  // The guide is answering: no new message can go out (INV-PH-137), so no ✕ either.
  const sendLocked = useSendLocked();

  return (
    <li className={cn('plan__row', `plan__row--${item.kind}`, removing && 'plan__row--removing')} aria-busy={removing}>
      <span
        className={cn(
          'plan__marker',
          numbered ? 'plan__marker--num' : ['plan__marker--glyph', `plan__marker--${item.kind}`],
        )}
        aria-hidden="true"
      >
        {numbered ? item.n : item.kind === 'stop' ? '•' : null}
      </span>
      <div className="plan__body">
        <div className="plan__head">
          {numbered || item.kind === 'stop' ? null : (
            <span className="visually-hidden">{KIND_WORD[item.kind]}: </span>
          )}
          <span className="plan__title">{plain(item.title)}</span>
          {item.hours ? <span className="plan__time">{plain(item.hours)}</span> : null}
          {statusLabel ? <span className="plan__badge">{statusLabel}</span> : null}
        </div>
        {item.subtitle ? <div className="plan__subtitle">{plain(item.subtitle)}</div> : null}
        {item.note ? <div className="plan__note">{plain(item.note)}</div> : null}
        {typeof item.from_prev_m === 'number' ? (
          // Metres from the PREVIOUS point, which is what the graph measured —
          // saying what a number measures is the whole fix behind it.
          <div className="plan__walk">~{item.from_prev_m} м от предыдущей точки</div>
        ) : null}
        {item.tickets_url ? (
          <a
            className="plan__ticket"
            href={item.tickets_url}
            target="_blank"
            rel="noreferrer noopener"
          >
            Билет
          </a>
        ) : null}
      </div>
      {item.kind === 'stop' && numbered && editable ? (
        removing ? (
          <span className="plan__removing" role="status">
            Убираю…
          </span>
        ) : (
          <button
            type="button"
            className="plan__remove"
            title={`Убрать «${plain(item.title)}» из плана`}
            disabled={locked || sendLocked}
            // The phrase is the graph's own removal wording, sent verbatim — it is
            // what `remove_from_plan`'s parser reads on the canvas family; on
            // `peterhof_agent` it is an ordinary utterance its model reads. A press
            // is answered at once and held until the turn answers (`planRemoval`).
            onClick={() => requestRemoval(item.title)}
          >
            ✕<span className="visually-hidden"> убрать «{plain(item.title)}»</span>
          </button>
        )
      ) : null}
      {item.kind === 'break' && after && editable ? (
        <button
          type="button"
          className="plan__remove plan__remove--break"
          disabled={sendLocked}
          title={`Убрать перерыв после «${plain(after)}»`}
          // An ordinary utterance the model reads (INV-PH-08) — `plan_edit(op=break, value=off, anchor, point)`.
          onClick={() => send(breakRemoveAsk(item.title, after))}
        >
          ✕<span className="visually-hidden"> убрать перерыв после «{plain(after)}»</span>
        </button>
      ) : null}
    </li>
  );
}

/** The title of the stop a break at *position* follows — the nearest stop above it in the day. */
function stopBefore(items: PlanItem[], position: number): string | undefined {
  for (let i = position - 1; i >= 0; i -= 1) {
    if (items[i]!.kind === 'stop') return items[i]!.title;
  }
  return undefined;
}

function Day({
  day,
  index,
  showHeading,
  editable,
}: {
  day: PlanDay;
  index: number;
  showHeading: boolean;
  editable: boolean;
}) {
  const items = day.items ?? [];
  if (items.length === 0) return null;
  const heading = day.label ?? day.date ?? `День ${index + 1}`;
  return (
    <section className="plan__section">
      {showHeading ? <h4 className="plan__section-title">{plain(heading)}</h4> : null}
      <ol className="plan__list">
        {items.map((item, position) => (
          <Row
            key={`${item.title}:${position}`}
            item={item}
            editable={editable}
            after={item.kind === 'break' ? stopBefore(items, position) : undefined}
          />
        ))}
      </ol>
    </section>
  );
}

export default function PlanTimeline({ props, extra }: { props: TripPlanProps; extra?: ReactNode }) {
  const sendLocked = useSendLocked();
  // A stop's `✕` edits the CURRENT plan: on a snapshot under an older answer
  // (a board-less graph's chat, #132) it would edit today's plan from
  // yesterday's picture, so there the snapshot is read-only (`inlineCard.ts`).
  const editable = useCardLive(props.id);
  const days = props.days.filter((day) => (day.items ?? []).length > 0);
  // One day is a walk and needs no heading over it; several are a trip.
  const showHeadings = days.length > 1;

  const totals = planTotals(props);

  return (
    <article className="card card--plan">
      <header className="card__head">
        <h3 className="card__title">{plain(props.title ?? 'План поездки')}</h3>
        {props.date ? <span className="card__meta">{plain(props.date)}</span> : null}
      </header>

      {totals ? <p className="plan__totals">{totals}</p> : null}

      {days.length > 0 ? (
        days.map((day, index) => (
          <Day
            key={day.date ?? day.label ?? index}
            day={day}
            index={index}
            showHeading={showHeadings}
            editable={editable}
          />
        ))
      ) : (
        <p className="card__empty">Пока пусто — расскажите, когда и надолго ли вы приедете.</p>
      )}

      {/* `peterhof_agent`'s own: «В следующий раз» and the export (#39, #50). */}
      {extra}

      {/* §11.5: «Изменить маршрут» / «Убрать точку» / «Билеты по плану» — the
          card's own actions, distinct from a stop's own «✕ убрать» above. */}
      <CardActions sourceId={props.id} actions={props.actions} />

      {/* «Удалить план» (#170). Since the plan outlives the conversation (#169),
          dropping it has to be reachable without asking to be forgotten entirely.
          Like the header's «Забыть меня» it sends the ASK and nothing else: the
          graph answers with its own confirmation card, and only that card's «Да»
          deletes. The phrase is generated from the tool that reads it
          (`ci/export_ui_phrases.py`) — rewording it here breaks it in silence.
          Only on a live snapshot: an old copy would delete a plan it is not showing. */}
      {editable && days.length > 0 ? (
        <footer className="plan__foot">
          <button
            type="button"
            className="btn btn--quiet plan__clear"
            disabled={sendLocked}
            title="Удалить план целиком — гид переспросит"
            onClick={() => send(phrases.plan.clear_ask)}
          >
            Удалить план
          </button>
        </footer>
      ) : null}
    </article>
  );
}
